import {AfterViewInit, Component, ElementRef, Inject, OnDestroy, ViewChild, ViewEncapsulation} from '@angular/core';
import {NgIf} from '@angular/common';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatTooltipModule} from '@angular/material/tooltip';
import Cropper from 'cropperjs';

export interface UserAvatarCropperDialogData {
    file: File;
    displayName?: string;
}

export interface UserAvatarCropperDialogResult {
    file: File;
}

@Component({
    selector: 'user-avatar-cropper-dialog',
    templateUrl: './user-avatar-cropper-dialog.component.html',
    styleUrls: ['./user-avatar-cropper-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [NgIf, MatButtonModule, MatDialogModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule]
})
export class UserAvatarCropperDialogComponent implements AfterViewInit, OnDestroy {

    @ViewChild('image', {static: true}) image: ElementRef<HTMLImageElement>;

    imageUrl: string;
    processing = false;
    errorMessage: string | null = null;
    private cropper: Cropper;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: UserAvatarCropperDialogData,
        private dialogRef: MatDialogRef<UserAvatarCropperDialogComponent, UserAvatarCropperDialogResult>
    ) {
        this.imageUrl = URL.createObjectURL(data.file);
    }

    ngAfterViewInit(): void {
        this.cropper = new Cropper(this.image.nativeElement, {
            aspectRatio: 1,
            autoCropArea: 0.92,
            background: false,
            center: true,
            dragMode: 'move',
            guides: false,
            minContainerHeight: 360,
            minContainerWidth: 320,
            responsive: true,
            restore: false,
            viewMode: 1
        });
    }

    ngOnDestroy(): void {
        this.cropper?.destroy();
        URL.revokeObjectURL(this.imageUrl);
    }

    zoomIn(): void {
        this.cropper?.zoom(0.1);
    }

    zoomOut(): void {
        this.cropper?.zoom(-0.1);
    }

    rotateLeft(): void {
        this.cropper?.rotate(-90);
    }

    rotateRight(): void {
        this.cropper?.rotate(90);
    }

    reset(): void {
        this.cropper?.reset();
    }

    crop(): void {
        if (!this.cropper) {
            return;
        }

        this.processing = true;
        this.errorMessage = null;

        const canvas = this.cropper.getCroppedCanvas({
            fillColor: '#ffffff',
            height: 512,
            imageSmoothingEnabled: true,
            imageSmoothingQuality: 'high',
            width: 512
        });

        if (!canvas) {
            this.processing = false;
            this.errorMessage = 'Das Bild konnte nicht zugeschnitten werden.';
            return;
        }

        canvas.toBlob((blob) => {
            this.processing = false;

            if (!blob) {
                this.errorMessage = 'Das Bild konnte nicht vorbereitet werden.';
                return;
            }

            const fileName = this.buildFileName(this.data.file.name);
            const file = new File([blob], fileName, {type: 'image/jpeg', lastModified: Date.now()});
            this.dialogRef.close({file});
        }, 'image/jpeg', 0.88);
    }

    private buildFileName(fileName: string): string {
        const cleanName = (fileName || 'avatar')
            .replace(/\.[^/.]+$/, '')
            .replace(/[^a-zA-Z0-9-_]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .toLowerCase();

        return `${cleanName || 'avatar'}-avatar.jpg`;
    }
}
